import { useQuery } from "@tanstack/react-query";

import type { ShipmentManagement } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { shipmentManagementSchema } from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

import type { DashboardQueryParams } from "./dashboard-query-params.ts";
import { buildDashboardSearchParams } from "./dashboard-query-params.ts";

const emptyShipmentManagement: ShipmentManagement = {
	exceptions: { immediateActionCount: 0, needsAcknowledgedCount: 0 },
	statuses: { notDispatchedDatedToday: 0, pastPickupsNotYetTracking: 0 },
	shipmentProgress: {
		todaysPickups: { inGoodStanding: 0, outstandingIssues: 0 },
		inTransit: { inGoodStanding: 0, outstandingIssues: 0 },
		todaysDeliveries: { inGoodStanding: 0, outstandingIssues: 0 },
	},
};

export const useShipmentManagement = (
	params: Pick<
		DashboardQueryParams,
		"ltlBrokerIds" | "primaryBrokerIds" | "customerIds"
	>,
) => {
	const { data, isLoading, isFetching, error, refetch } = useQuery({
		queryKey: [
			"dashboardShipmentManagement",
			params.ltlBrokerIds ?? [],
			params.primaryBrokerIds ?? [],
			params.customerIds ?? [],
		],
		queryFn: async ({ signal }) => {
			const searchParams = buildDashboardSearchParams(params);
			const url = `${apiRoutes.DASHBOARD_SHIPMENT_MANAGEMENT}?${searchParams.toString()}`;
			const result = await getAndValidateTraxAr(url, shipmentManagementSchema, {
				signal,
			});
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	return {
		data: data ?? emptyShipmentManagement,
		isLoading,
		isFetching,
		error,
		refetch,
	};
};
